'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarPlus, CalendarCheck, X } from 'lucide-react'
import { useFocusTrap } from '@/lib/use-focus-trap'
import type { Tournament } from '@/lib/tournaments'
import {
  googleCalendarUrl,
  outlookCalendarUrl,
  downloadIcs,
  type CalendarEvent,
} from '@/lib/calendar'

/** End time: last schedule item + 1h, otherwise 3h after the start. */
function eventEnd(t: Tournament): string {
  const lastStart = t.schedule?.[t.schedule.length - 1]?.start
  const base = lastStart ? new Date(lastStart) : new Date(t.startsAt)
  const minutes = lastStart ? 60 : 180
  return new Date(base.getTime() + minutes * 60_000).toISOString()
}

export function AddToCalendarButton({ t }: { t: Tournament }) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(open, dialogRef)

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const ev: CalendarEvent = {
    title: t.title,
    description: t.description,
    location: t.region,
    start: t.startsAt,
    end: eventEnd(t),
    url: typeof window !== 'undefined' ? window.location.href : t.discordUrl ?? undefined,
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-primary/50"
      >
        <CalendarPlus className="size-4" /> Add to Calendar
      </button>

      {open && mounted && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Add to calendar"
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            className="w-full max-w-sm overflow-hidden rounded-t-2xl border border-border bg-card p-6 shadow-2xl outline-none sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div className="flex size-11 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/30">
                <CalendarCheck className="size-5 text-primary" />
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
              >
                <X className="size-5" />
              </button>
            </div>

            <h2 className="text-lg font-bold">Add to Calendar</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              You&apos;re registered — add {t.title} to your calendar so you don&apos;t
              miss it. The lobby code is shared 5 minutes before start.
            </p>

            <div className="mt-5 space-y-2.5">
              <a
                href={googleCalendarUrl(ev)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/google-calendar.svg" alt="" className="size-6 shrink-0" />
                Google Calendar
              </a>
              <a
                href={outlookCalendarUrl(ev)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/outlook.svg" alt="" className="size-6 shrink-0" />
                Outlook.com
              </a>
              <button
                onClick={() => {
                  downloadIcs(ev, t.slug)
                  setOpen(false)
                }}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/icons/apple-calendar.svg" alt="" className="size-6 shrink-0" />
                Apple Calendar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
